/**
 * Call Metrics Tracker
 * Handles client-side metrics tracking for call sessions
 */

// Debounce utility for efficient updates
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Event types for logging
export const EVENT_TYPES = {
  // Connection events
  CALL_STARTED: 'call_started',
  CALL_ENDED: 'call_ended',
  CONNECTION_QUALITY_CHANGED: 'connection_quality_changed',
  
  // User interactions
  TRANSCRIPT_CLICKED: 'transcript_clicked',
  WORD_TRANSLATED: 'word_translated',
  VOCAB_SAVED: 'vocab_saved',
  
  // Settings changes
  SETTINGS_CHANGED: 'settings_changed',
  VOICE_CHANGED: 'voice_changed',
  SPEED_CHANGED: 'speed_changed',
  THEME_CHANGED: 'theme_changed',
  
  // Audio events
  USER_MUTED: 'user_muted',
  USER_UNMUTED: 'user_unmuted',
  TRANSCRIPTION_TOGGLED: 'transcription_toggled',
  
  // Errors
  CONNECTION_ERROR: 'connection_error',
  TRANSCRIPT_ERROR: 'transcript_error',
  API_ERROR: 'api_error'
};

// Quality score mapping for LiveKit connection quality
export const QUALITY_SCORES = {
  'excellent': 5,
  'good': 4,
  'poor': 2,
  'lost': 1,
  'unknown': 3
};

/**
 * Base class for tracking call metrics
 */
export class CallMetricsTracker {
  constructor(callSessionId, apiClient) {
    this.callSessionId = callSessionId;
    this.apiClient = apiClient;
    this.metrics = {
      transcript_word_count: 0,
      transcript_clicks: 0,
      last_connection_quality: null,
      settings_snapshot: null
    };
    this.pendingUpdates = {};
    this.updateQueue = [];
    this.isUpdating = false;
    
    // Debounced update function
    this.debouncedUpdate = debounce(this.flushUpdates.bind(this), 5000);
  }

  /**
   * Update a metric value
   */
  updateMetric(key, value) {
    this.metrics[key] = value;
    this.pendingUpdates[key] = value;
    
    // Immediate update for critical metrics
    if (key === 'last_connection_quality' && value <= 2) {
      this.flushUpdates();
    } else {
      this.debouncedUpdate();
    }
  }

  /**
   * Update multiple metrics at once
   */
  updateMetrics(updates) {
    Object.entries(updates).forEach(([key, value]) => {
      this.metrics[key] = value;
      this.pendingUpdates[key] = value;
    });
    this.debouncedUpdate();
  }

  /**
   * Flush pending updates to the server
   */
  async flushUpdates() {
    if (Object.keys(this.pendingUpdates).length === 0 || this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    const updates = { ...this.pendingUpdates };
    this.pendingUpdates = {};

    try {
      await this.apiClient.updateCallSessionMetrics(this.callSessionId, updates);
    } catch (error) {
      console.error('Failed to update metrics:', error);
      // Re-add failed updates to pending
      Object.assign(this.pendingUpdates, updates);
      
      // Retry after delay
      setTimeout(() => {
        this.isUpdating = false;
        this.flushUpdates();
      }, 10000);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Force flush all pending updates (call on unmount)
   */
  async forceFlush() {
    // Cancel debounced update
    this.debouncedUpdate.cancel?.();
    
    // Flush immediately
    await this.flushUpdates();
  }

  /**
   * Log an event
   */
  async logEvent(eventType, eventData = {}) {
    try {
      await this.apiClient.createCallEventLog(this.callSessionId, {
        event_type: eventType,
        event_data: eventData,
        occurred_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log event:', error);
      // Queue for retry
      this.updateQueue.push({
        type: 'event',
        data: { eventType, eventData },
        timestamp: Date.now()
      });
    }
  }

  /**
   * Get current metrics
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Cleanup tracker
   */
  async cleanup() {
    await this.forceFlush();
    this.updateQueue = [];
  }
}

/**
 * Transcript metrics tracking
 */
export class TranscriptTracker {
  constructor(metricsTracker) {
    this.metricsTracker = metricsTracker;
    this.wordCount = 0;
    this.clickCount = 0;
    this.lastUpdateTime = Date.now();
  }

  /**
   * Update word count from transcript
   */
  updateWordCount(transcriptText) {
    if (!transcriptText) return;
    
    const words = transcriptText.split(/\s+/).filter(word => word.length > 0);
    const newCount = words.length;

    if (newCount !== this.wordCount) {
      this.wordCount = newCount;
      this.metricsTracker.updateMetric('transcript_word_count', newCount);
    }
  }

  /**
   * Track a click on the transcript
   */
  trackClick() {
    this.clickCount++;
    this.metricsTracker.updateMetric('transcript_clicks', this.clickCount);
    this.metricsTracker.logEvent(EVENT_TYPES.TRANSCRIPT_CLICKED, {
      click_count: this.clickCount,
      timestamp: Date.now()
    });
  }

  /**
   * Track word translation
   */
  trackWordTranslation(word, translation, context) {
    // Handle context being either a string or an object with sentence property
    const contextSentence = typeof context === 'string' ? context : context?.sentence;
    
    this.metricsTracker.logEvent(EVENT_TYPES.WORD_TRANSLATED, {
      word,
      translation,
      context: contextSentence ? contextSentence.substring(0, 100) : null, // Limit context length
      language: context?.language
    });
  }

  /**
   * Reset tracker
   */
  reset() {
    this.wordCount = 0;
    this.clickCount = 0;
  }
}

/**
 * Settings snapshot tracker
 */
export class SettingsTracker {
  constructor(metricsTracker) {
    this.metricsTracker = metricsTracker;
    this.lastSnapshot = null;
  }

  /**
   * Capture current settings snapshot
   */
  captureSnapshot(settings) {
    const snapshot = {
      voice: settings.voice,
      voice_provider: settings.voice_provider,
      speed: settings.speed,
      speed_multiplier: settings.speed_multiplier,
      language: settings.language,
      theme: settings.theme || 'light',
      transcription_enabled: settings.showTranscription !== false,
      auto_send: settings.auto_send,
      auto_record: settings.auto_record,
      keep_screen_on: settings.keep_screen_on,
      captured_at: new Date().toISOString()
    };

    this.lastSnapshot = snapshot;
    this.metricsTracker.updateMetric('settings_snapshot', snapshot);
    return snapshot;
  }

  /**
   * Track a setting change
   */
  trackSettingChange(settingName, oldValue, newValue, settings) {
    this.metricsTracker.logEvent(EVENT_TYPES.SETTINGS_CHANGED, {
      setting: settingName,
      old_value: oldValue,
      new_value: newValue
    });

    // Capture new snapshot after change
    setTimeout(() => {
      this.captureSnapshot(settings);
    }, 1000);
  }
}

/**
 * Connection quality tracker
 */
export class ConnectionQualityTracker {
  constructor(metricsTracker) {
    this.metricsTracker = metricsTracker;
    this.lastQuality = null;
    this.qualityHistory = [];
    this.debouncedUpdate = debounce(this.updateQuality.bind(this), 5000);
  }

  /**
   * Track quality change
   */
  onQualityChanged(quality) {
    const qualityScore = QUALITY_SCORES[quality] || 3;
    
    // Add to history
    this.qualityHistory.push({
      quality,
      score: qualityScore,
      timestamp: Date.now()
    });

    // Keep only last 100 entries
    if (this.qualityHistory.length > 100) {
      this.qualityHistory.shift();
    }

    // Update immediately for poor connections
    if (quality === 'poor' || quality === 'lost') {
      this.updateQuality(qualityScore);
      this.metricsTracker.logEvent(EVENT_TYPES.CONNECTION_QUALITY_CHANGED, {
        quality,
        score: qualityScore
      });
    } else {
      // Debounce updates for good connections
      this.debouncedUpdate(qualityScore);
    }

    this.lastQuality = quality;
  }

  /**
   * Update quality metric
   */
  updateQuality(score) {
    this.metricsTracker.updateMetric('last_connection_quality', score);
  }

  /**
   * Get average quality over time window
   */
  getAverageQuality(windowMs = 60000) {
    const now = Date.now();
    const recentEntries = this.qualityHistory.filter(
      entry => (now - entry.timestamp) <= windowMs
    );

    if (recentEntries.length === 0) return null;

    const sum = recentEntries.reduce((acc, entry) => acc + entry.score, 0);
    return Math.round(sum / recentEntries.length);
  }
}